/**
 * Constants for the MapLibre **route** map components. Kept in one place (rather than scattered across the
 * component files) so sizes, spacing and options can be tuned together.
 *
 * Engine-generic constants live in `../maplibre/` — this file is route-block-specific.
 */

/**
 * A line needs at least this many points to exist as a line — to be drawn, to carry arrows, or to be
 * simplified. One shared constant because every part of this module meant the same thing by it.
 */
export const MIN_LINE_POINTS = 2;

/** Gap (px) between repeated arrows along a line (Leaflet ≈ "►" + ~25 spaces at 14px). */
export const ROUTE_ARROW_SYMBOL_SPACING_PX = 90;

/**
 * Gap (px) for the GPS trail's arrows — deliberately NOT a multiple of the planned route's spacing.
 *
 * The trail runs a few px alongside the planned route it was driven on, so with one shared spacing both
 * arrow rows land at the same distances along the same shape: every planned arrow gets a twin ~6 px away,
 * which reads as one doubled, smudged glyph rather than two lines with direction. Out-of-phase spacings can
 * only line up once per LCM (1260 px, so at most once across a screen) and the trail's own wander breaks
 * even that. The sparser rhythm also suits a line that already carries direction in its dots and car
 * marker, and it trims symbols on a crowded map.
 */
export const TRAIL_ARROW_SYMBOL_SPACING_PX = 140;

/** Prefix for the per-route-color arrow icon registered on the map via `addImage`. */
export const ROUTE_ARROW_IMAGE_ID_PREFIX = "ryde-route-arrow-";
/** Canvas resolution (px) the arrow glyph is drawn at before `addImage`. */
export const ROUTE_ARROW_IMAGE_SIZE_PX = 32;
/** `addImage` pixelRatio — the canvas renders at `ROUTE_ARROW_IMAGE_SIZE_PX / ratio` CSS px. */
export const ROUTE_ARROW_IMAGE_PIXEL_RATIO = 1.2;
/** `icon-size` multiplier applied to the registered arrow image. */
export const ROUTE_ARROW_ICON_SIZE = 1;
/** Extra `icon-rotate` (deg) on top of the auto line-bearing — 0 for a right-pointing glyph. */
export const ROUTE_ARROW_ICON_ROTATE_DEGREES = 0;

/** Stroke width (px) of the GPS trail lines; also the divisor for dash-string → MapLibre units. */
export const TRAIL_LINE_WIDTH_PX = 4;

/** Angle maths shared by the arrow renderers and the trail-dot bearing helper. */
export const HALF_TURN_DEGREES = 180;
export const FULL_TURN_DEGREES = 360;
export const DEGREES_PER_RADIAN = HALF_TURN_DEGREES / Math.PI;

/** Gap (px) between a trail dot and its hover tooltip. */
export const TRAIL_TOOLTIP_OFFSET_PX = 12;

/** Gap (px) between the vehicle marker and its tracking popup. */
export const TRACKING_POPUP_OFFSET_PX = 24;
/** How far down (px) to nudge the map when the tracking popup opens, so the popup lands in frame. */
export const TRACKING_POPUP_VERTICAL_OFFSET_PX = 96;
export const TRACKING_POPUP_EASE_DURATION_MS = 250;

/**
 * Below this separation (deg, ~1.1 m) two GPS samples describe jitter rather than travel. A stopped vehicle
 * emits runs of identical positions — on a real ride a quarter of the points can be this close — and a
 * bearing taken from them is 0 or noise, which throws a dot's offset off in an arbitrary direction.
 */
export const MIN_BEARING_SEPARATION_DEG = 1e-5;
/** How far to walk outward looking for a separated neighbour before giving up (bounds a long stop). */
export const BEARING_SCAN_LIMIT = 60;

/** Prefix for the baked GPS-trail-dot icons registered on the map via `addImage`. */
export const TRAIL_DOT_IMAGE_ID_PREFIX = "ryde-trail-dot-";
/** `addImage` pixelRatio for the dot icons — drawn above 1 so they stay crisp on hi-DPI screens. */
export const TRAIL_DOT_IMAGE_PIXEL_RATIO = 2;

/** GPS trail dot radius (px). */
export const TRAIL_POINT_RADIUS_PX = 4;
/** GPS trail dot radius (px) for detour points. */
export const TRAIL_POINT_DETOUR_RADIUS_PX = 5;

/**
 * From this many routes on one map the per-route treatments switch to their cheap variants: stations render
 * as GPU circles instead of one HTML `<Marker>` each (a crowded live map carries ~48 stops per ride, and
 * MapLibre repositions every marker individually per frame), and the GPS trail's arrows and dots wait for a
 * zoom where they are readable rather than being re-laid out for every trail on every poll.
 *
 * Deliberately set well above a dispatcher's normal working selection rather than at the first sign of load.
 * Comparing a dozen or two rides is routine work, and those screens keep their numbered pins and final-stop
 * flags; the cheap variants are for the whole-board selections that actually stall the map.
 *
 * The planned route's direction arrows are NOT one of these trades. They are static geometry riding the
 * line's own source, so they stay on at every zoom and every selection size — see `maplibre-route-resolver`.
 */
export const CROWDED_MAP_MIN_ROUTES = 25;
